<template>
  <div class="d-flex align-center" data-locator="pagination-actions">
    <div v-if="!hidePageSize" class="d-flex align-center mr-6">
      <span class="text-caption mr-4">
        <slot name="label">
          {{ $t('common.pagination.linesPerPage') }}
        </slot>
      </span>
      <AppSelect :model-value="pageSize"
                 class="page-size-select"
                 :items="pageSizeOptions"
                 no-label
                 variant="underlined"
                 data-locator="max-items-select"
                 @update:model-value="pageSizeChanged"
      />
    </div>

    <v-pagination :model-value="currentPage"
                  :length="computedLength"
                  :size="32"
                  :total-visible="7"
                  active-color="primary"
                  density="comfortable"
                  variant="elevated"
                  prev-icon="angle-left"
                  next-icon="angle-right"
                  @update:model-value="currentPageChanged"
    />
  </div>
</template>

<script>
import AppSelect from '@/common/AppSelect.vue'

const DEFAULT_PAGE_SIZE_OPTIONS = [25, 50, 100, 250]

/**
 * Default Pagination
 * @displayName AppPagination
 */
/**
 * A wrapper around the pagination component with default styling and custom default behavior
 * All Vuetify props for the pagination component are usable.
 * @see https://vuetifyjs.com/en/api/v-pagination/#props
 */
export default {
  name: 'AppPagination',
  components: { AppSelect },
  inheritAttrs: false,
  emits: [
    'update:current-page',
    'update:page-size',
  ],
  props: {
    currentPage: {
      type: Number,
      required: true,
    },
    /**
     * Items per page
     */
    pageSize: {
      type: Number,
      required: true,
    },
    /**
     * Total items count
     */
    total: {
      type: Number,
      required: true,
    },
    /**
     * Items per page options
     * @default [50, 100, 250]
     */
    pageSizes: {
      type: Array,
      default: () => DEFAULT_PAGE_SIZE_OPTIONS,
    },
    /**
     * Hide the page size selector
     */
    hidePageSize: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    computedLength () {
      return this.total !== 0
          ? Math.ceil(this.total / this.pageSize)
          : 0
    },
    pageSizeOptions () {
      return this.pageSizes.map(size => ({ title: size, value: size }))
    },
  },
  methods: {
    currentPageChanged (page) {
      this.$emit('update:current-page', page)
    },
    pageSizeChanged (pageSize) {
      this.$emit('update:page-size', pageSize)
    },
  },
}
</script>

<style scoped lang="scss">
:deep(*) {
  .page-size-select.v-input {
    .v-input__control {
      margin-top: -12px;
    }

    .v-field {
      align-items: center;

      &__field {
        font-size: 12px;
      }

      &__append-inner {
        font-size: 10px;
      }

      &__input {
        .v-select__selection {
          margin: 0;
        }
      }
    }
  }

  .v-pagination {
    &__prev,
    &__next,
    &__item {
      margin-top: 0;
      margin-bottom: 0;
    }

    &__prev,
    &__next {
      font-size: 10px;
      color: rgb(var(--v-theme-accent));

      .v-btn--disabled {
        background: rgb(var(--v-theme-white));
      }
    }

    &__prev {
      margin-right: 10px;
    }
    &__next {
      margin-left: 10px;
      padding: 0;
    }
  }
}
</style>
